import { ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';
import { setContext } from '@apollo/client/link/context';
import { gqlUrl } from 'lib/apiUrl';
import {
  getAuthDataFromCookies,
  getAuthDataFromSessionStorage,
} from 'src/utils/authUtils';

const httpLink = createHttpLink({
  uri: gqlUrl,
  credentials: 'include',
});

let logout: () => void = () => {};

const retryLink = new RetryLink({
  attempts: {
    max: 2,
    retryIf: (error, _operation) => {
      if (error.statusCode === 401) {
        logout();
        return true;
      }
      return false;
    },
  },
});

const authLink = setContext((_, { headers }) => {
  // sessionStorage/document are browser-only; bail on SSR to avoid crashes
  // if a query ever fires server-side against the singleton client.
  if (typeof window === 'undefined') {
    return { headers };
  }
  const { jwt } = getAuthDataFromSessionStorage().jwt
    ? getAuthDataFromSessionStorage()
    : getAuthDataFromCookies();
  return {
    headers: {
      ...headers,
      authorization: jwt ? `Bearer ${jwt}` : '',
    },
  };
});

export const client = new ApolloClient({
  link: retryLink.concat(authLink).concat(httpLink),
  cache: new InMemoryCache(),
  defaultOptions: {
    query: {
      fetchPolicy: 'no-cache',
      context: {},
    },
  },
});

// export const createApolloClient = () => {
//   return new ApolloClient({
//     uri: 'link',
//     cache: new InMemoryCache(),
//   });
// };

// TODO: we need to add the following code to the client.ts file in the apolloClient folder, but i can break a lot of logic in the app, so we need to test it first
// export const client = new ApolloClient({
//   link: retryLink.concat(authLink).concat(httpLink),
//   cache: new InMemoryCache({
//     resultCaching: false, Setting resultCaching: false tells Apollo not to cache the results of your queries, which can be useful if you want every network request to be fresh. This is particularly useful for queries that return data that changes frequently, like stock prices or weather data.
//   }),
//   defaultOptions: {
//     watchQuery: {
//       fetchPolicy: 'no-cache', When you create your client, you can specify that both regular queries and watched queries (which include fetchMore) use a fetch policy of 'no-cache'. For example:
//     },
//     query: {
//       fetchPolicy: 'no-cache',
//     },
//   },
// });

export const setLogoutHandler = (logoutHandler: () => void) => {
  logout = logoutHandler;
};

/**
 * Creates a fresh Apollo client for SSR/ISR usage.
 * Each server-side request should use its own client to avoid
 * cache bleed between concurrent requests.
 * No auth link — server-side queries are unauthenticated by design.
 */
export function createServerClient(): ApolloClient<object> {
  return new ApolloClient({
    link: httpLink,
    cache: new InMemoryCache(),
    ssrMode: true,
    defaultOptions: {
      query: {
        fetchPolicy: 'no-cache',
      },
    },
  });
}
